import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { AxiosResponse } from "axios";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { CommonDrawerStyle, DrawerHeader } from "../../../Common/styles/drawer";
import { InputWrapper, LabelStyle } from "../../../Common/styles/form";
import { ModalActionButtonStyles } from "../../../Common/styles/modal";
import http from "../../../../utils/http";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../../Common/styles/table";
import { MoreVert } from "@mui/icons-material";
import { setParticipantToggle } from "../../../../Redux/reducers/participantsSlice";
import { useAppDispatch } from "../../../../Redux/hooks";

const allowedTypes = [
  "radio",
  "checkbox",
  "dropdown",
  "text",
  "number",
  "date",
  "slider",
  "calculated_field",
];

type Props = {
  closeDrawer: () => void;
};

const schema = yup.object().shape({
  form: yup.string().required("Form is Required"),
  question: yup.string().required("Question is Required"),
  headerName: yup
    .string()
    .trim()
    .max(50, "Header Name should not exceed 50 characters")
    .required("Header Name is Required"),
});

const AddCustomFieldDrawer: React.FC<Props> = ({ closeDrawer }) => {
  const { id: studyId } = useParams();
  const dispatch = useAppDispatch();

  const [formList, setFormList] = useState<any>([]);
  const [questionList, setQuestionList] = useState<any>([]);
  const [selectedFields, setSelectedFields] = useState<any[]>([]);

  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldLoading, setFieldLoading] = useState<boolean>(false);
  const [questionLoading, setQuestionLoading] = useState<boolean>(false);

  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedFormId, setSelectedFormId] = useState<string>("");
  const [toggle, setToggle] = useState<boolean>(false);
  const [isHeaderUpdated, setIsHeaderUpdated] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editLabel, setEditLabel] = useState<string>("");
  const [editLoading, setEditLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchFieldsData = async () => {
      try {
        setFieldLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/settings/participant-custom-columns`,
        );
        const fieldRes = res.data?.data;
        let listData = fieldRes?.map((item: any) => ({
          headerName: item?.label,
          questionId: item?.questionId,
          id: item?.id,
          question: item?.question?.varname,
        }));
        setSelectedFields(listData);
        setFieldLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setFieldLoading(false);
      }
    };
    fetchFieldsData();
  }, [studyId, toggle]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const url = `/study/${studyId}/study-phase?category=screening`;
        const res: AxiosResponse = await http.get(url);
        let screeningListData = [];
        const data = res.data?.data?.[0];
        if (data) {
          const phaseRes: AxiosResponse = await http.get(
            `/study/${studyId}/study-phase/${data?.id}`,
          );
          const phaseData = phaseRes.data?.data;
          screeningListData = (phaseData.phaseForms || [])
            .sort((a: any, b: any) => a.position - b.position)
            .filter(
              (phaseForm: any) =>
                phaseForm?.form.status === "active" ||
                phaseForm?.form.status === "inactive",
            )
            .map((phaseForm: any) => ({
              formId: phaseForm?.form?.id,
              name: phaseForm?.form?.name || "",
            }));
        }
        const formListRes: AxiosResponse = await http.get(
          `/study/${studyId}/study-phase?category=visit`,
        );
        const formRes = formListRes.data?.data;
        const sortedPhases = formRes?.sort(
          (a: any, b: any) => a.position - b.position,
        );
        const listData = sortedPhases?.flatMap((phase: any) =>
          (phase.phaseForms || [])
            .sort((a: any, b: any) => a.position - b.position)
            .map((phaseForm: any) => ({
              formId: phaseForm?.form?.id,
              name: phaseForm?.form?.name || "",
            })),
        );
        setFormList([...screeningListData, ...listData]);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    fetchData();
  }, [studyId]);

  useEffect(() => {
    const fetchQuestionData = async () => {
      try {
        setQuestionLoading(true);
        const questionListRes: AxiosResponse = await http.get(
          `/study/${studyId}/forms/${selectedFormId}`,
        );
        const questionRes = questionListRes.data?.data?.questions;
        let listData = questionRes
          ?.filter((q: any) => allowedTypes.includes(q?.type))
          .sort((a: any, b: any) => a.position - b.position)
          .map((q: any) => ({
            questionId: q?.id,
            question: q?.label || "",
            varname: q?.varname || "",
          }));
        setQuestionList(listData);
        setQuestionLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setQuestionLoading(false);
      }
    };
    if (selectedFormId) {
      fetchQuestionData();
    } else {
      setQuestionList([]);
      setQuestionLoading(false);
    }
  }, [studyId, selectedFormId]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };
  const handleDelete = async () => {
    try {
      setFieldLoading(true);
      let url: string = `/study/${studyId}/settings/participant-custom-columns/${selectedRow?.id}`;
      let res: AxiosResponse;

      res = await http.delete(url);

      toastMessage("success", res.data.message);
      setIsHeaderUpdated(true);
      refreshTable();
      setFieldLoading(false);
    } catch (err) {
      setFieldLoading(false);
      errorToastMessage(err as Error);
    }
  };
  const refreshTable = () => {
    setToggle((prev) => !prev);
  };

  const openEditModal = () => {
    setEditLabel(selectedRow?.headerName || "");
    setEditModalOpen(true);
    setAnchorEl(null);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditLabel("");
    setSelectedRow(null);
  };

  const handleEdit = async () => {
    if (!editLabel.trim()) {
      toastMessage("warning", "Header Name is required.");
      return;
    }
    if (editLabel.trim().length > 50) {
      toastMessage("warning", "Header Name should not exceed 50 characters.");
      return;
    }
    try {
      setEditLoading(true);
      const url = `/study/${studyId}/settings/participant-custom-columns/${selectedRow?.id}/header`;
      const res: AxiosResponse = await http.patch(url, {
        label: editLabel.trim(),
      });
      toastMessage("success", res.data.message);
      setIsHeaderUpdated(true);
      refreshTable();
      closeEditModal();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleAdd = async (values: any, resetForm: any) => {
    const alreadyAdded = selectedFields.some(
      (item: any) => item.questionId === values.question,
    );
    if (alreadyAdded) {
      toastMessage("warning", "This question is already added.");
      return;
    }

    try {
      setSubmitLoader(true);
      let url: string = `/study/${studyId}/settings/participant-custom-columns`;
      let res: AxiosResponse;

      let body: any = {
        questionId: values.question,
        label: values.headerName,
        displayType: "label",
      };

      res = await http.post(url, body);

      toastMessage("success", res.data.message);
      refreshTable();
      setIsHeaderUpdated(true);
      setSubmitLoader(false);
      resetForm();
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };

  const handleAddAll = async () => {
    const questionsToAdd = questionList.filter(
      (q: any) =>
        !selectedFields.some((sf: any) => sf.questionId === q.questionId),
    );

    if (questionsToAdd.length === 0) {
      toastMessage(
        "warning",
        "All questions from this form are already added.",
      );
      return;
    }

    try {
      setSubmitLoader(true);
      const url = `/study/${studyId}/settings/participant-custom-columns/bulk`;
      const payload = questionsToAdd.map((question: any) => ({
        questionId: question.questionId,
        label: question.varname,
        displayType: "label",
      }));
      const res = await http.post(url, { columns: payload });

      toastMessage("success", res.data.message);
      refreshTable();
      setIsHeaderUpdated(true);
      setSubmitLoader(false);
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };

  const handleClose = () => {
    if (isHeaderUpdated) {
      dispatch(setParticipantToggle());
    }
    closeDrawer();
  };

  return (
    <Box sx={CommonDrawerStyle}>
      <DrawerHeader closeDrawer={handleClose} title="Custom Field" />
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ overflow: "auto", pr: 0.5 }}>
          <Formik
            initialValues={{
              form: "",
              question: "",
              headerName: "",
            }}
            enableReinitialize
            onSubmit={(values, { resetForm }) => {
              handleAdd(values, resetForm);
            }}
            validationSchema={schema}
          >
            {({ getFieldProps, errors, touched, values, setFieldValue }) => (
              <Form>
                {!loading ? (
                  <Box>
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle} htmlFor="form">
                        Form <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <Select
                        value={values.form}
                        onChange={(e) => {
                          setFieldValue("form", e.target.value);
                          setSelectedFormId(e.target.value);
                          setFieldValue("question", "");
                        }}
                        fullWidth
                        id="form"
                      >
                        {formList.length > 0 ? (
                          formList.map((option: any) => (
                            <MenuItem key={option.formId} value={option.formId}>
                              {option.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="">No forms available</MenuItem>
                        )}
                      </Select>
                      <FormHelperText
                        error={touched?.form && errors?.form ? true : false}
                      >
                        {touched?.form && errors?.form
                          ? (errors?.form as string)
                          : " "}
                      </FormHelperText>
                    </FormControl>
                    {!questionLoading ? (
                      <FormControl sx={InputWrapper}>
                        <FormLabel sx={LabelStyle} htmlFor="question">
                          Question <span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        <Select
                          value={values.question}
                          onChange={(e) => {
                            setFieldValue("question", e.target.value);
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: "300px",
                                overflowY: "auto",
                              },
                            },
                          }}
                          fullWidth
                          id="question"
                          disabled={!values.form}
                        >
                          {questionList.length > 0 ? (
                            questionList.map((option: any) => (
                              <MenuItem
                                key={option.questionId}
                                value={option.questionId}
                              >
                                {option.question.length > 100
                                  ? option.question.slice(0, 100) + "..."
                                  : option.question}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="">No questions available</MenuItem>
                          )}
                        </Select>
                        <FormHelperText
                          error={
                            touched?.question && errors?.question ? true : false
                          }
                        >
                          {touched?.question && errors?.question
                            ? (errors?.question as string)
                            : " "}
                        </FormHelperText>
                      </FormControl>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          height: "72px",
                        }}
                      >
                        <CircularProgress size={25} />
                      </Box>
                    )}
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle} htmlFor="header-name">
                        Header Name <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <TextField
                        fullWidth
                        placeholder="Header Name"
                        id="header-name"
                        {...getFieldProps("headerName")}
                        inputProps={{ maxLength: 50 }}
                        error={
                          touched?.headerName && errors?.headerName
                            ? true
                            : false
                        }
                        helperText={
                          touched?.headerName && errors?.headerName
                            ? (errors?.headerName as string)
                            : " "
                        }
                      />
                    </FormControl>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mb: 1,
                        height: "40px",
                      }}
                    >
                      {!submitLoader ? (
                        <>
                          {values.form && (
                            <Button variant="outlined" onClick={handleAddAll}>
                              Add All
                            </Button>
                          )}
                          <Button variant="outlined" type="submit">
                            Add
                          </Button>
                        </>
                      ) : (
                        <Box sx={{ height: "30px", width: "30px" }}>
                          <CircularProgress size={25} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Skeleton
                      variant="text"
                      height={21}
                      width={45}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rounded" height={46} sx={{ mb: 2.5 }} />
                    <Skeleton
                      variant="text"
                      height={21}
                      width={45}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rounded" height={46} sx={{ mb: 2.5 }} />
                    <Skeleton
                      variant="text"
                      height={21}
                      width={45}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rounded" height={46} sx={{ mb: 2.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Skeleton variant="rounded" height={40} width={80} />
                    </Box>
                  </Box>
                )}
              </Form>
            )}
          </Formik>
          <Box sx={{ maxHeight: "300px", overflow: "auto", mt: 4 }}>
            <Table sx={TableBorderRadiusTopLeftRight}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Header Name</StyledTableCell>
                  <StyledTableCell>Question</StyledTableCell>
                  <StyledTableCell />
                </TableRow>
              </TableHead>
              {!fieldLoading && selectedFields?.length > 0 && (
                <TableBody>
                  {selectedFields?.map((row: any) => (
                    <TableRow key={row?.id}>
                      <StyledTableCell>{row?.headerName}</StyledTableCell>
                      <StyledTableCell>{row?.question || "-"}</StyledTableCell>
                      <StyledTableCell align="right">
                        <IconButton onClick={(e) => handleMenuClick(e, row)}>
                          <MoreVert />
                        </IconButton>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!fieldLoading && selectedFields?.length === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}
            {fieldLoading && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ mt: "auto" }}>
          <Divider />
          <Box sx={{ ...ModalActionButtonStyles, mt: 2, mb: 2 }}>
            <Button variant="contained" onClick={handleClose}>
              Done
            </Button>
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <MenuItem onClick={openEditModal}>Edit</MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            handleDelete();
          }}
          sx={{ color: "#F05252" }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={editModalOpen}
        onClose={closeEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { p: 1 } }}
      >
        <DialogTitle>Edit Header Name</DialogTitle>
        <DialogContent>
          <FormControl sx={InputWrapper}>
            <FormLabel sx={LabelStyle} htmlFor="header-name">
              Header Name <span style={{ color: "red" }}>*</span>
            </FormLabel>
            <TextField
              autoFocus
              fullWidth
              placeholder="Header Name"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              inputProps={{ maxLength: 50 }}
              sx={{ mt: 1 }}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddCustomFieldDrawer;
